/**
 * Listen button (voice layer V1): renders TTS for a piece of guide text.
 * One per assistant message / learn card; playing state is per-key so only
 * the active button shows the stop affordance.
 */

import React, { useEffect, useState } from 'react';
import { Loader2, Square, Volume2 } from 'lucide-react';
import VoicePlaybackService from '../services/VoicePlaybackService';

const VoiceListenButton = ({
  text,
  playbackKey,
  projectId,
  chatId,
  messageId,
  title,
  className = '',
  size = 15,
}) => {
  const [playback, setPlayback] = useState(VoicePlaybackService.getState());

  useEffect(() => VoicePlaybackService.subscribe(setPlayback), []);

  if (!text || !String(text).trim()) return null;

  const mine = playback.key === playbackKey;
  const status = mine ? playback.status : 'idle';

  const onClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    VoicePlaybackService.unlock();
    if (mine && status !== 'idle') {
      VoicePlaybackService.stop();
    } else {
      VoicePlaybackService.speak({
        text,
        key: playbackKey,
        projectId,
        chatId,
        messageId,
        title,
      });
    }
  };

  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center justify-center p-1.5 rounded-full transition-colors ${
        status !== 'idle'
          ? 'text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-500/10'
          : 'text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/60'
      } ${className}`}
      title={status !== 'idle' ? 'Stop' : 'Listen'}
      aria-label={status !== 'idle' ? 'Stop narration' : 'Listen to this'}
    >
      {status === 'loading' ? (
        <Loader2 size={size} className="animate-spin" />
      ) : status === 'playing' ? (
        <Square size={size - 3} fill="currentColor" />
      ) : (
        <Volume2 size={size} />
      )}
    </button>
  );
};

export default VoiceListenButton;
